import { FX_RATES_QUERY_KEY, setFxRatesSnapshot } from "@/lib/fxRates";
import { QueryClient, dehydrate, hydrate } from "@tanstack/react-query";
import { createRouter } from "@tanstack/react-router";
import { routeTree } from "./routeTree.gen";
import { trackPageView } from "./lib/posthog";

export const getRouter = async () => {
  // Analytics must not block router creation or hydration.
  // Ported from the pre-migration src/App.tsx QueryClient config.
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 2 * 60 * 1000, // 2 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes
        retry: 1,
        refetchOnWindowFocus: false,
        refetchOnReconnect: "always",
        refetchOnMount: "always",
      },
    },
  });

  const router = createRouter({
    routeTree,
    context: { queryClient },
    scrollRestoration: true,
    // Preload route code + loader data as soon as the user shows intent
    // (hover/focus/touchstart) so navigation feels instant.
    defaultPreload: "intent",
    defaultPreloadDelay: 50,
    // React Query owns data freshness; let it decide, not the router cache.
    defaultPreloadStaleTime: 0,
    // The app reads query params with URLSearchParams (react-router style), so
    // keep them as plain strings. TanStack's default JSON serializer would
    // write ?page="2" (quoted), which breaks parseInt and pagination.
    parseSearch: (searchStr: string) =>
      Object.fromEntries(new URLSearchParams(searchStr)) as Record<string, string>,
    stringifySearch: (search: Record<string, unknown>) => {
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(search ?? {})) {
        if (value === undefined || value === null) continue;
        params.set(key, typeof value === "string" ? value : String(value));
      }
      const str = params.toString();
      return str ? `?${str}` : "";
    },

    // Ship the server-fetched React Query cache to the client so SSR-rendered
    // stats don't flash back to empty during hydration. The big listings
    // payloads are excluded — they'd bloat the HTML by megabytes; the client
    // refetches them instead.
    dehydrate: () => ({
      queryClientState: dehydrate(queryClient, {
        shouldDehydrateQuery: (query) => query.queryKey?.[0] !== "listings-v2:v2",
      }) as unknown as Record<string, never>,
    }),
    hydrate: (dehydrated: { queryClientState?: unknown }) => {
      if (dehydrated?.queryClientState) {
        hydrate(queryClient, dehydrated.queryClientState as never);
        // Same fx_rates the server used, so SSR and hydrated conversions match.
        setFxRatesSnapshot(queryClient.getQueryData(FX_RATES_QUERY_KEY) as never);
      }
    },


  });

  if (typeof window !== "undefined") {
    router.subscribe("onResolved", ({ toLocation }) => {
      trackPageView(new URL(toLocation.href, window.location.origin).href);
    });
  }

  return router;
};

